import React from 'react';
import { Shield } from 'lucide-react';

const TermsOfService = () => {
  const lastUpdated = new Date().toLocaleDateString();

  return (
    <div className="bg-black min-h-screen text-gray-300 pt-20 px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center mb-12">
          <Shield className="w-16 h-16 mr-6 text-white" />
          <h1 className="text-5xl font-bold text-white tracking-tight">
            Terms of Service
          </h1>
        </div>

        <div className="space-y-12">
          {/* Introduction */}
          <div className="bg-gray-900 p-6 rounded-lg">
            <p className="text-lg leading-relaxed">
              Welcome to ContextUI (the "Service"), brought to you by Intelligent System Design Pty Ltd ("Company," "we," "us," or "our"). Please read these Terms of Service ("Terms") carefully. By accessing or using the Service, you agree to be bound by these Terms.
            </p>
            <p className="mt-4 text-lg leading-relaxed">
              If you do not agree to all of these Terms, you may not access or use the Service.
            </p>
          </div>

          {/* Section 1 */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">1. Acceptance of Terms</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">1.1 Binding Agreement</h3>
                <p className="text-lg leading-relaxed">
                  These Terms form a legally binding agreement between you and Intelligent System Design Pty Ltd. By using ContextUI, you represent that you have read, understood, and agree to be bound by these Terms, as well as our <a href="/privacy" className="text-blue-400 hover:text-blue-300">Privacy Policy</a>.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">1.2 Modifications</h3>
                <p className="text-lg leading-relaxed">
                  We may modify or update these Terms from time to time. We will notify you of any material changes through the Service interface when you next access ContextUI. Your continued use of the Service after being notified of changes indicates your acceptance of the revised Terms.
                </p>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">2. Eligibility</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">2.1 Age Requirement</h3>
                <p className="text-lg leading-relaxed">
                  To create an account and use the paid features of the Service, you must be at least 16 years old. Users under 16 may use certain features of the Service with parental or guardian consent and supervision. By using ContextUI, you represent that you meet these requirements.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">2.2 Compliance With Laws</h3>
                <p className="text-lg leading-relaxed">
                  You agree to comply with all applicable local, state, national, and international laws and regulations regarding your use of the Service.
                </p>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">3. Account Registration and Responsibilities</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">3.1 Account Creation</h3>
                <p className="text-lg leading-relaxed">
                  To access certain features, you may be required to create an account. You agree to provide accurate, current, and complete information during registration and to keep such information updated.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">3.2 Account Security</h3>
                <p className="text-lg leading-relaxed">
                  You are responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your account. If you suspect any unauthorized use or security breach, please contact us immediately at support@isd.ai.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">3.3 Prohibited Activities</h3>
                <p className="text-lg leading-relaxed">
                  You agree not to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Use the Service for any illegal or unauthorized purpose.</li>
                  <li>Interfere with or disrupt the Service, servers, or networks connected to the Service.</li>
                  <li>Post or transmit any malicious code, viruses, or harmful software.</li>
                  <li>Harvest or collect personal data of other users without their consent.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">4. Description of Service</h2>
            
            <div className="space-y-4">
              <div>
                <p className="text-lg leading-relaxed">
                  ContextUI is an advanced AI visualization and analysis platform designed to illuminate the internal mechanisms of artificial intelligence systems. Our tools enable researchers, engineers, and AI practitioners to:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-4">
                  <li>Build and configure neural network models through an intuitive interface</li>
                  <li>Visualize complex AI architectures with interactive node-based exploration</li>
                  <li>Train models with powerful optimization and hyperparameter tuning capabilities</li>
                  <li>Analyze model behavior and understand the mechanistic interpretability of AI systems</li>
                  <li>Detect potential biases and harmful behaviors within AI models</li>
                  <li>Optimize networks through weight manipulation and model pruning</li>
                </ul>
                <p className="text-lg leading-relaxed mt-4">
                  We provide these capabilities through our subscription-based software service, supporting both individual and institutional users in advancing the field of AI interpretability and safety. We reserve the right to modify, enhance, or discontinue any part of the Service at any time to improve user experience.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">4.1 No Warranty for AI Outcomes or Interpretations</h3>
                <p className="text-lg leading-relaxed">
                  While ContextUI provides tools for AI interpretation and analysis, we do not guarantee specific outcomes, interpretations, or insights. AI models remain complex systems, and the Service is intended as a research and development tool to assist in understanding. Users should exercise their own judgment when making decisions based on visualizations or analytics provided by the Service.
                </p>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">5. Intellectual Property Rights</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">5.1 Our Content</h3>
                <p className="text-lg leading-relaxed">
                  The ContextUI desktop application source code is licensed under the Apache License 2.0 and may be used, modified, and distributed in accordance with that license. Trademarks, logos, and branding related to ContextUI remain the property of Intelligent System Design Pty Ltd and may not be used without prior written consent except as permitted by the Apache 2.0 license for attribution purposes. Other content and materials (including text, graphics, and documentation on this website) are owned by or licensed to Intelligent System Design Pty Ltd.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">5.2 User-Generated Content</h3>
                <p className="text-lg leading-relaxed">
                  If the Service allows you to submit or post content ("User Content"), you retain ownership of your User Content. However, by submitting such User Content, you grant Intelligent System Design Pty Ltd a worldwide, non-exclusive, royalty-free license to use, reproduce, process, adapt, publish, transmit, and display your content in connection with operating ContextUI.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">5.3 Models and Insights</h3>
                <p className="text-lg leading-relaxed">
                  You retain all intellectual property rights to any models, weights, visualizations, or insights derived from your use of the Service. However, Intelligent System Design Pty Ltd may collect anonymized, aggregated data about software usage patterns to improve the Service. This anonymized data will not include your models, weights, training data, or specific visualizations.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">5.4 Third-Party Integrations</h3>
                <p className="text-lg leading-relaxed">
                  The Service may integrate with or allow connections to third-party services. These integrations are subject to the third parties' own terms of service and privacy policies. We make no representations regarding third-party services and shall not be liable for any issues arising from your use of such services.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">5.5 Copyright Complaints</h3>
                <p className="text-lg leading-relaxed">
                  If you believe that your work has been used in a way that constitutes copyright infringement, please notify us at support@isd.ai with all relevant details.
                </p>
              </div>
            </div>
          </section>

          {/* Section 6 */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">6. Fees and Payment</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">6.1 Subscription Plans</h3>
                <p className="text-lg leading-relaxed">
                  Any fees for using ContextUI will be displayed before you agree to pay. By choosing a subscription, you agree to pay the specified fees according to your selected plan (monthly, annual, or educational/institutional). We reserve the right to change our pricing structure with reasonable notice before renewal periods.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">6.2 Payment Method</h3>
                <p className="text-lg leading-relaxed">
                  You must provide a valid payment method (e.g., credit card, PayPal). You authorize us to charge your payment method for all due fees. You are responsible for keeping your payment information current.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">6.3 Subscription Renewal and Cancellation</h3>
                <p className="text-lg leading-relaxed">
                  Subscriptions automatically renew unless canceled prior to the renewal date. You may cancel your subscription at any time through your account settings or by contacting support. Upon cancellation, your subscription will remain active until the end of your current billing period, after which it will not renew.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">6.4 Free Trial</h3>
                <p className="text-lg leading-relaxed">
                  We may offer free trial periods for certain subscription plans. At the conclusion of a trial period, your account will automatically convert to a paid subscription unless you cancel before the trial ends. You will be charged the applicable subscription fee at the beginning of the first billing cycle after the trial ends.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">6.5 Refund Policy</h3>
                <p className="text-lg leading-relaxed">
                  Unless otherwise required by applicable law, all fees are non-refundable. If you believe you're entitled to a refund, please contact our support team at support@isd.ai.
                </p>
              </div>
            </div>
          </section>

          {/* Section 7 */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">7. Term and Termination</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">7.1 Term</h3>
                <p className="text-lg leading-relaxed">
                  These Terms are effective as long as you continue to use the Service.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">7.2 Termination by You</h3>
                <p className="text-lg leading-relaxed">
                  You may stop using the Service at any time. If you wish to cancel your subscription or delete your account, you can do so through your account settings or by contacting us at support@isd.ai.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">7.3 Termination by Us</h3>
                <p className="text-lg leading-relaxed">
                  We reserve the right to suspend or terminate your account or access to the Service at any time if you violate these Terms or if we deem it necessary. Upon termination, you must cease all use of ContextUI.
                </p>
              </div>
            </div>
          </section>

          {/* Section 8 */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">8. Disclaimer of Warranties</h2>
            
            <div className="space-y-4">
              <div>
                <p className="text-lg leading-relaxed uppercase font-bold">
                  CONXTEXUI IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. TO THE FULLEST EXTENT PERMITTED BY LAW, INTELLIGENT SYSTEM DESIGN PTY LTD DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </p>
                <p className="text-lg leading-relaxed mt-4">
                  We do not warrant that the Service will be uninterrupted, error-free, secure, or free of viruses or other harmful components.
                </p>
              </div>
            </div>
          </section>

          {/* Section 9 */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">9. Limitation of Liability</h2>
            
            <div className="space-y-4">
              <div>
                <p className="text-lg leading-relaxed uppercase font-bold">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, INTELLIGENT SYSTEM DESIGN PTY LTD (INCLUDING ITS DIRECTORS, OFFICERS, EMPLOYEES, AND AGENTS) SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES.
                </p>
                <p className="text-lg leading-relaxed mt-4 uppercase font-bold">
                  IN NO EVENT SHALL INTELLIGENT SYSTEM DESIGN PTY LTD'S TOTAL LIABILITY FOR ALL CLAIMS EXCEED THE AMOUNT YOU PAID (IF ANY) FOR ACCESSING THE SERVICE IN THE SIX (6) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM, OR USD $100, WHICHEVER IS GREATER.
                </p>
              </div>
            </div>
          </section>

          {/* Section 10 */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">10. Indemnification</h2>
            
            <div className="space-y-4">
              <div>
                <p className="text-lg leading-relaxed">
                  You agree to defend, indemnify, and hold harmless Intelligent System Design Pty Ltd and its directors, officers, employees, and agents from and against any third-party claims, damages, obligations, losses, liabilities, costs, or debt, and expenses (including attorney's fees) arising from:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-4">
                  <li>Your use of and access to ContextUI.</li>
                  <li>Your violation of these Terms.</li>
                  <li>Your infringement of any third party's rights, including intellectual property or privacy rights.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 11 */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">11. Governing Law and Dispute Resolution</h2>
            
            <div className="space-y-4">
              <div>
                <p className="text-lg leading-relaxed">
                  These Terms will be governed by and construed in accordance with the laws of New South Wales, Australia, without regard to its conflict of laws principles. Any dispute arising out of or related to these Terms or the Service shall be brought in the courts located in New South Wales, Australia, and you consent to their exclusive jurisdiction and venue.
                </p>
                <p className="text-lg leading-relaxed mt-4">
                  Notwithstanding the above, if you are a consumer, you may be entitled to certain mandatory consumer protection rights in your country of residence. Nothing in these Terms limits your rights as a consumer under such mandatory provisions of local law. Additionally, attempts to resolve disputes may be required through local dispute resolution mechanisms in certain jurisdictions before proceeding to litigation.
                </p>
              </div>
            </div>
          </section>

          {/* Section 12 */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">12. Changes to the Service</h2>
            
            <div className="space-y-4">
              <div>
                <p className="text-lg leading-relaxed">
                  We reserve the right to change, update, or discontinue the Service (or any part of it) at any time without notice. We are not liable for any modification, suspension, or discontinuance of the Service.
                </p>
              </div>
            </div>
          </section>

          {/* Section 13 */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">13. Data Protection and Privacy</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">13.1 Privacy Policy</h3>
                <p className="text-lg leading-relaxed">
                  Our <a href="/privacy" className="text-blue-400 hover:text-blue-300">Privacy Policy</a> describes how we handle the information we collect from you when you use the Service. By using ContextUI, you also agree to the terms of our Privacy Policy.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">13.2 Data Processing</h3>
                <p className="text-lg leading-relaxed">
                  Where applicable, we act as a data processor for any customer data processed through our Service. We will only process such data in accordance with your instructions and as necessary to provide the Service.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">13.3 Security Measures</h3>
                <p className="text-lg leading-relaxed">
                  We implement appropriate technical and organizational measures to protect your data. However, no security system is impenetrable, and we cannot guarantee the absolute security of your information. You are responsible for maintaining the security of your account credentials.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">13.4 Data Export and Portability</h3>
                <p className="text-lg leading-relaxed">
                  You may export your data from the Service in commonly used formats. Upon termination of your account, we will retain your data only for as long as necessary to comply with our legal obligations or resolve disputes.
                </p>
              </div>
            </div>
          </section>

          {/* Section 14 */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">14. Miscellaneous</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">14.1 Entire Agreement</h3>
                <p className="text-lg leading-relaxed">
                  These Terms, along with our Privacy Policy, constitute the entire agreement between you and Intelligent System Design Pty Ltd regarding ContextUI and supersede any prior agreements.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">14.2 Severability</h3>
                <p className="text-lg leading-relaxed">
                  If any provision of these Terms is found to be invalid or unenforceable, that provision shall be severed, and the remaining provisions shall remain in full force and effect.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">14.3 Waiver</h3>
                <p className="text-lg leading-relaxed">
                  No waiver of any provision in these Terms by Intelligent System Design Pty Ltd shall be deemed a further or continuing waiver of such provision or any other provision.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">14.4 Assignment</h3>
                <p className="text-lg leading-relaxed">
                  You may not assign or transfer your rights or obligations under these Terms without our prior written consent. We may freely assign or transfer these Terms at our discretion.
                </p>
              </div>
            </div>
          </section>

          {/* Section 15 - Additional Terms */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">15. Additional Terms</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">15.1 API Usage</h3>
                <p className="text-lg leading-relaxed">
                  If you access our APIs (Application Programming Interfaces), you agree to:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Use the APIs only as permitted and within usage limits we establish</li>
                  <li>Not circumvent any rate limits or other access restrictions</li>
                  <li>Ensure your use of our APIs complies with our documentation and these Terms</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">15.2 Academic and Research Use</h3>
                <p className="text-lg leading-relaxed">
                  We encourage academic and research use of ContextUI. If you're using the Service for academic or research purposes, you agree to:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Properly cite ContextUI in any publications or presentations featuring results obtained using our Service</li>
                  <li>Not misrepresent the capabilities or limitations of ContextUI in your research</li>
                  <li>Comply with your institution's research ethics requirements</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">15.3 Beta Features</h3>
                <p className="text-lg leading-relaxed">
                  We may offer beta, preview, or experimental features. Such features are provided "as is" without warranty of any kind, and we may change or discontinue them at any time without notice. Your use of beta features is at your own risk.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">15.4 Export Compliance</h3>
                <p className="text-lg leading-relaxed">
                  You agree to comply with all applicable export control laws and regulations when using the Service. You represent that you are not located in, or a resident of, any country subject to applicable trade sanctions or embargoes.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">15.5 Service Availability</h3>
                <p className="text-lg leading-relaxed">
                  While we strive to maintain high availability, we do not guarantee uninterrupted access to the Service. Scheduled maintenance and updates may occasionally impact availability. For customers with specific uptime requirements, separate service level agreements may be available upon request.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">15.6 Third-Party API Keys and Services</h3>
                <p className="text-lg leading-relaxed">
                  ContextUI may allow you to use your own API keys from third-party services (such as Anthropic, OpenAI, or other generative AI providers). Regarding such usage:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>You are solely responsible for obtaining, managing, and securing your third-party API keys</li>
                  <li>You must comply with all terms and conditions of the third-party service providers</li>
                  <li>You bear all costs associated with your use of third-party APIs, including any charges or fees imposed by those providers</li>
                  <li>Intelligent System Design Pty Ltd is not responsible for any content generated through third-party services</li>
                  <li>We have no control over and assume no responsibility for availability, rate limits, quotas, or other aspects of third-party APIs</li>
                  <li>We are not liable for any consequences resulting from the suspension, termination, or change of your access to third-party services</li>
                  <li>You indemnify us against any claims arising from your use of third-party services in conjunction with our platform</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">15.7 Open Source and Third-Party Libraries</h3>
                <p className="text-lg leading-relaxed">
                  ContextUI incorporates various open source and third-party software libraries. A complete list of these libraries and their licenses can be found on our <a href="/attributions" className="text-blue-400 hover:text-blue-300">Attributions</a> page.
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>These components are subject to their respective licenses as detailed on the Attributions page</li>
                  <li>We provide these attributions in compliance with the respective license requirements</li>
                  <li>No warranty is provided for any open source components beyond what is specified in their respective licenses</li>
                  <li>We are not liable for any issues arising from the use of these open source components</li>
                  <li>Any modifications we make to open source components comply with their respective licenses</li>
                  <li>Nothing in these Terms restricts rights granted to you under open source licenses applicable to the relevant components</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">15.8 Exchange Marketplace</h3>
                <p className="text-lg leading-relaxed">
                  The ContextUI Exchange allows users to publish, share, and download workflows created by third parties. Regarding your use of the Exchange:
                </p>
                <p className="text-lg leading-relaxed mt-4 font-semibold text-white">For all users:</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Workflows available on the Exchange are created by independent users, not by Intelligent System Design Pty Ltd</li>
                  <li>We do not review, endorse, or guarantee the functionality, safety, or legality of third-party workflows</li>
                  <li>You are solely responsible for evaluating whether a workflow is suitable for your purposes</li>
                  <li>You must ensure your use of any workflow complies with all applicable third-party terms of service (e.g., APIs, websites, or services the workflow interacts with)</li>
                  <li>We are not liable for any damages, losses, or legal claims arising from your use of third-party workflows</li>
                </ul>
                <p className="text-lg leading-relaxed mt-4 font-semibold text-white">For creators publishing workflows:</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>You are solely responsible for ensuring your workflow does not infringe any third-party intellectual property rights</li>
                  <li>You must comply with the license terms of any open source libraries, code, or assets incorporated into your workflow</li>
                  <li>You must ensure any APIs, data sources, or external services your workflow connects to permit such use under their terms of service</li>
                  <li>You represent that you have the right to distribute all components included in your workflow</li>
                  <li>You agree not to upload workflows that facilitate illegal activity or violate third-party terms of service</li>
                  <li>We reserve the right to remove any workflow from the Exchange at our discretion</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 16 - Contact */}
          <section className="border-l-4 border-white pl-6">
            <h2 className="text-3xl font-semibold text-white mb-6">16. Contact Us</h2>
            
            <div className="space-y-4">
              <div>
                <p className="text-lg leading-relaxed">
                  If you have questions, comments, or concerns about these Terms or ContextUI, please contact us at:
                </p>
                <p className="text-lg mt-4">
                  Intelligent System Design Pty Ltd<br />
                  Email: <a href="mailto:support@isd.ai" className="text-blue-400 hover:text-blue-300">support@isd.ai</a>
                </p>
              </div>
            </div>
          </section>

          <div className="mt-16 text-center">
            <p className="text-sm text-gray-500 italic">
              Last Updated: {lastUpdated}
            </p>
            <p className="mt-4 text-sm text-gray-400 uppercase tracking-wider">
              BY USING CONTEXTUI, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS OF SERVICE.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;